import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import Breadcrumb from "@/components/Breadcrumb";
import AdSlot from "@/components/AdSlot";
import { ArrowsClockwise, ArrowsLeftRight, CurrencyDollar } from "@phosphor-icons/react";

// Public free FX API — no key required
const FX_URL = "https://open.er-api.com/v6/latest/";

const POPULAR = ["USD", "EUR", "GBP", "JPY", "INR", "CNY", "CAD", "AUD", "CHF", "SGD", "HKD", "AED", "BRL", "MXN", "ZAR", "KRW"];

const FLAGS = {
  USD: "🇺🇸", EUR: "🇪🇺", GBP: "🇬🇧", JPY: "🇯🇵", INR: "🇮🇳",
  CNY: "🇨🇳", CAD: "🇨🇦", AUD: "🇦🇺", CHF: "🇨🇭", SGD: "🇸🇬",
  HKD: "🇭🇰", AED: "🇦🇪", BRL: "🇧🇷", MXN: "🇲🇽", ZAR: "🇿🇦",
  KRW: "🇰🇷", NZD: "🇳🇿", SEK: "🇸🇪", NOK: "🇳🇴", DKK: "🇩🇰",
  RUB: "🇷🇺", TRY: "🇹🇷", THB: "🇹🇭", IDR: "🇮🇩", PHP: "🇵🇭",
  MYR: "🇲🇾", VND: "🇻🇳", PKR: "🇵🇰", EGP: "🇪🇬", NGN: "🇳🇬",
};

export default function CurrencyToolPage() {
  const [base, setBase] = useState("USD");
  const [target, setTarget] = useState("EUR");
  const [amount, setAmount] = useState("100");
  const [rates, setRates] = useState({});
  const [allCodes, setAllCodes] = useState([]);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchRates = async (b = base) => {
    setLoading(true);
    try {
      const res = await fetch(FX_URL + b);
      const data = await res.json();
      if (data && data.rates) {
        setRates(data.rates);
        setAllCodes(Object.keys(data.rates).sort());
        setUpdatedAt(data.time_last_update_utc || new Date().toUTCString());
      } else {
        toast.error("Rates unavailable — try again shortly.");
      }
    } catch {
      toast.error("Unable to load rates. Check your connection.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRates(base);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base]);

  const converted = useMemo(() => {
    const a = parseFloat(amount);
    if (isNaN(a) || !rates[target]) return 0;
    return a * rates[target];
  }, [amount, target, rates]);

  const swap = () => {
    setBase(target);
    setTarget(base);
  };

  return (
    <>
      <SEO
        title="Live Currency Rates"
        description="Free live currency converter. Convert between 160+ world currencies with up-to-date foreign exchange rates."
        url="/tools/currency"
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Breadcrumb items={[{ label: "Home", to: "/" }, { label: "Tools", to: "/tools" }, { label: "Currency" }]} />
      </div>

      <header className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 border-b-2 border-foreground pb-8">
        <div className="overline text-primary inline-flex items-center gap-2">
          <CurrencyDollar size={12} weight="bold" /> Tool 02
        </div>
        <h1 className="font-display font-black text-4xl md:text-6xl tracking-tight mt-2">
          Live Currency Rates
        </h1>
        <p className="mt-3 max-w-2xl text-foreground/70">
          Convert between 160+ world currencies. Rates refresh on demand from a public FX feed.
        </p>
      </header>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid gap-8 lg:grid-cols-12">
        {/* Converter */}
        <div className="lg:col-span-7 space-y-6">
          <div className="border-2 border-foreground p-6 md:p-8" data-testid="fx-converter">
            <div className="grid gap-4 md:grid-cols-[1fr_auto_1fr] items-end">
              {/* From */}
              <div>
                <label className="overline block mb-2">You send</label>
                <div className="flex border border-foreground/25 focus-within:border-foreground">
                  <input
                    data-testid="fx-amount"
                    type="number"
                    step="0.01"
                    min="0"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className="flex-1 h-14 px-4 bg-transparent outline-none text-2xl font-display font-bold"
                  />
                  <select
                    data-testid="fx-base"
                    value={base}
                    onChange={(e) => setBase(e.target.value)}
                    className="h-14 px-3 bg-muted border-l border-foreground/25 outline-none font-mono font-semibold"
                  >
                    {(allCodes.length ? allCodes : POPULAR).map((c) => (
                      <option key={c} value={c}>
                        {FLAGS[c] ? FLAGS[c] + "  " : ""}{c}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Swap button */}
              <button
                onClick={swap}
                data-testid="fx-swap"
                aria-label="Swap currencies"
                className="hidden md:inline-flex items-center justify-center w-12 h-12 border border-foreground/25 hover:bg-foreground hover:text-background transition-colors duration-200 mb-1"
              >
                <ArrowsLeftRight size={18} weight="bold" />
              </button>

              {/* To */}
              <div>
                <label className="overline block mb-2">Recipient gets</label>
                <div className="flex border border-foreground/25 focus-within:border-foreground">
                  <input
                    data-testid="fx-converted"
                    readOnly
                    value={converted.toFixed(4)}
                    className="flex-1 h-14 px-4 bg-transparent outline-none text-2xl font-display font-bold text-primary"
                  />
                  <select
                    data-testid="fx-target"
                    value={target}
                    onChange={(e) => setTarget(e.target.value)}
                    className="h-14 px-3 bg-muted border-l border-foreground/25 outline-none font-mono font-semibold"
                  >
                    {(allCodes.length ? allCodes : POPULAR).map((c) => (
                      <option key={c} value={c}>
                        {FLAGS[c] ? FLAGS[c] + "  " : ""}{c}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <button
              onClick={swap}
              className="md:hidden mt-4 w-full h-11 border border-foreground/25 inline-flex items-center justify-center gap-2"
            >
              <ArrowsLeftRight size={16} /> Swap
            </button>

            <div className="mt-6 pt-6 border-t border-foreground/15 flex items-baseline justify-between text-sm">
              <div className="font-mono text-foreground/70" data-testid="fx-rate">
                1 {base} = <span className="font-semibold text-foreground">{rates[target] ? rates[target].toFixed(4) : "…"}</span> {target}
              </div>
              <button
                onClick={() => fetchRates(base)}
                data-testid="fx-refresh"
                disabled={loading}
                className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-wider hover:text-primary transition-colors duration-200 disabled:opacity-40"
              >
                <ArrowsClockwise size={12} className={loading ? "animate-spin" : ""} />
                {loading ? "Refreshing" : "Refresh"}
              </button>
            </div>
            {updatedAt && (
              <div className="mt-2 text-[11px] font-mono text-foreground/50" data-testid="fx-updated">
                Last updated: {updatedAt}
              </div>
            )}
          </div>

          <AdSlot size="leaderboard" />
        </div>

        {/* Popular rates table */}
        <aside className="lg:col-span-5">
          <div className="border border-foreground/15">
            <div className="p-4 border-b border-foreground/15">
              <div className="overline">Popular rates vs {base}</div>
            </div>
            <div>
              {POPULAR.filter((c) => c !== base).map((c) => (
                <div
                  key={c}
                  data-testid={`fx-row-${c}`}
                  className="flex items-center justify-between px-4 py-3 border-b border-foreground/10 last:border-b-0 hover:bg-muted/40 transition-colors duration-200"
                >
                  <span className="inline-flex items-center gap-2 font-mono">
                    <span className="text-xl leading-none">{FLAGS[c] || ""}</span>
                    <span className="font-semibold">{c}</span>
                  </span>
                  <span className="font-mono text-sm">
                    {rates[c] ? rates[c].toFixed(4) : "…"}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <p className="mt-4 text-xs text-foreground/50 leading-relaxed">
            Rates provided by open.er-api.com for informational purposes. Not a
            quote for any specific transaction. Actual rates from banks and
            remittance services will include a spread.
          </p>
        </aside>
      </section>
    </>
  );
}
